// src/components/ui/badge.tsx
"use client"

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "lib/utils"

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 " +
    "transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 " +
    "[&>svg]:size-3 [&>svg]:pointer-events-none",
  {
    variants: {
      variant: {
        default:
          "bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400",
        secondary:
          "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-dark-600 dark:text-gray-200 dark:hover:bg-dark-500",
        success:
          "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
        warning:
          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
        error:
          "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
        info:
          "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
        outline:
          "border border-line-strong text-gray-700 hover:bg-gray-50 dark:border-dark-500 dark:text-gray-200 dark:hover:bg-dark-600",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  asChild?: boolean
}

function Badge({ className, variant, asChild = false, ...props }: BadgeProps) {
  const Comp = asChild ? Slot : "span"
  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
